import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api, { API_URL } from '../services/api';
import toast from '../utils/toast';
import FormSubmission from './FormSubmission';

const PublicForm = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const key = searchParams.get('key');

  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [submissionType, setSubmissionType] = useState('');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('publicFormToken');
    if (!token) {
      navigate(`/public-form/${slug}/auth?key=${key}`);
      return;
    }
    fetchForm();
  };

  const fetchForm = async () => {
    try {
      const token = localStorage.getItem('publicFormToken');
      const response = await axios.get(`${API_URL}/forms/public/${slug}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 401) {
        localStorage.removeItem('publicFormToken');
        navigate(`/forms/public/${slug}/auth?key=${key}`);
        return;
      }
      const formWithProducts = response.data.data;
      setForm(formWithProducts);
    } catch (error) {
      console.log("error", error);
      if (error.response?.status === 401) {
        localStorage.removeItem('publicFormToken');
        navigate(`/forms/public/${slug}/auth?key=${key}`);
      } else {
        toast.error(error.response?.data?.message || 'Failed to load form');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (submissionPayload) => {
    try {
      const token = localStorage.getItem('publicFormToken');
      
      // Extract data from the submission payload
      const formFields = submissionPayload.submissionData.formFields || [];
      const transformedProducts = submissionPayload.submissionData.products || [];
      const masterDataId = submissionPayload.masterDataId;
      const historyEntry = submissionPayload.historyEntry;
      
      // Convert formFields back to a simple object
      const formData = {};
      formFields.forEach(field => {
        formData[field.name] = field.value;
      });
      
      // Determine submission type based on form configuration
      const isTaskForm = form.schema && form.schema.task_data && form.schema.task_data.length > 0;
      const submissionType = isTaskForm ? 'task' : 'form_submission';
      setSubmissionType(submissionType);

      let submissionData = {
        formId: form._id,
        formName: form.name,
        formDescription: form.description,
        submissionData: {
          formFields: formData,
          products: transformedProducts,
          totalFees: submissionPayload.submissionData.totalFees || 0,
          totalProducts: submissionPayload.submissionData.totalProducts || 0
        },
        submittedAt: new Date(),
        lastUpdatedAt: new Date()
      };

      if (masterDataId) {
        submissionData.masterDataId = masterDataId;
        // Note: clientName and clientAddress would need to be fetched if needed
      }

      if (historyEntry) {
        submissionData.historyEntry = historyEntry;
      }

      let response;
      if (submissionType === 'task') {
        // Create task
        response = await api.post('/tasks', {
          name: form.name,
          description: form.description,
          client: masterDataId,
          deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
          formSubmissionData: submissionData,
          verificationTypes: form.verificationTypes || []
        }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      } else {
        // Create form submission
        response = await api.post(`/forms/public/${form.slug}/submit`, submissionData, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }

      setMessage({
        type: 'success',
        text: response.data.message || (submissionType === 'task' ? 'Task created successfully!' : 'Form submitted successfully!')
      });
      setSubmitted(true);

      // Return success result for FormSubmission component
      return {
        success: true,
        message: response.data.message || (submissionType === 'task' ? 'Task created successfully!' : 'Form submitted successfully!'),
        type: submissionType
      };

    } catch (error) {
      console.error('Submission error:', error);
        setMessage({
          type: 'error',
        text: error.response?.data?.message || 'Failed to submit form'
      });
      
      // Return error result for FormSubmission component
      throw error;
    }
  };

  const handleSubmitAnother = () => {
    setSubmitted(false);
    setMessage({ type: '', text: '' });
    // Reset form by re-fetching it
    fetchForm();
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Form Not Found</h2>
        <p className="text-gray-600">The form you're looking for doesn't exist or is not public.</p>
      </div>
    );
  }

  if (submitted) {
    const isTaskCreation = submissionType === 'task';

    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mt-4 text-2xl font-semibold text-gray-800">
              {isTaskCreation ? 'Task Created Successfully!' : 'Thank You!'}
            </h2>
            <p className="mt-2 text-gray-600 whitespace-pre-line">{message.text}</p>
          </div>
          <div className="flex justify-center space-x-4">
            <button
              onClick={handleSubmitAnother}
              className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {isTaskCreation ? 'Create Another Task' : 'Submit Another Response'}
            </button>
            <button
              onClick={handleCancel}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <FormSubmission
      form={form}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isPublic={true}
      apiCall={api}
      token={localStorage.getItem('publicFormToken')}
    />
  );
};

export default PublicForm;